import axios from 'axios';
import React, { useCallback } from 'react';
import { FieldErrors } from 'react-hook-form';
import * as yup from 'yup';
import {
  DUPLICATE_EMAIL_ERROR_CODE,
  useRegisterUser,
  useCreateUserProfile,
} from 'api';
import { useAuth } from 'ui/context/AuthContext';
import useAuthForm from 'ui/hooks/useAuthForm';
import { AuthSuccessResponse } from 'ui/types';
import { ensureAuthResponse } from 'ui/utils';
import { emailSchema, passwordSchema } from 'utils/schemas';
import { EMAIL_IN_USE_ERROR_MESSAGE, GENERAL_ERROR_MESSAGE } from './constants';

export interface SignUpFormData {
  name?: string;
  email: string;
  password: string;
}

export interface UseSignUpFormConfig {
  onError?: (errors: FieldErrors<SignUpFormData> | Error) => void;
  onAuthenticateSuccess?: (result: AuthSuccessResponse) => void;
}

type UseSignUpFormResult = Pick<
  ReturnType<typeof useAuthForm<SignUpFormData>>,
  'formState' | 'register' | 'validationState'
> & {
  hasInputError: boolean;
  isLoading: boolean;
  onSubmit: (e: React.SyntheticEvent) => Promise<void>;
};

const schema = yup.object().shape({
  email: emailSchema.required(),
  name: yup.string().optional(),
  password: passwordSchema.required(),
});

export default function useSignUpForm(
  config?: UseSignUpFormConfig,
): UseSignUpFormResult {
  const { onAuthenticateSuccess, onError } = config ?? {};

  const { promotionKey, referralKey, traceId } = useAuth();

  const { mutateAsync: registerUser, isLoading: isRegisteringUser } =
    useRegisterUser();
  const { mutateAsync: createUserProfile, isLoading: isCreatingUserProfile } =
    useCreateUserProfile();

  const {
    clearErrors,
    formState,
    handleSubmit,
    hasInputError,
    register,
    setError,
    validationState,
  } = useAuthForm<SignUpFormData>({
    schema,
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const submitForm = useCallback(
    async (data: Omit<SignUpFormData, 'unregisteredError'>) => {
      const { email, name, password } = data;

      clearErrors('unregisteredError');

      try {
        const registrationResponse = await registerUser({
          email,
          password,
          promotionKey,
          referralKey,
          traceId,
        });

        const response = ensureAuthResponse(registrationResponse);

        const { spareminToken, user } = response;

        await createUserProfile({
          email,
          spareminToken,
          userId: user.id,
          fullName: name,
        });

        onAuthenticateSuccess?.(response);
      } catch (err) {
        if (
          axios.isAxiosError(err) &&
          err.response?.data?.code === DUPLICATE_EMAIL_ERROR_CODE
        ) {
          const message = EMAIL_IN_USE_ERROR_MESSAGE;
          setError('email', { message }, { shouldFocus: true });
          onError?.(new Error(message));
        } else {
          const message = GENERAL_ERROR_MESSAGE;
          setError('unregisteredError', { message }, { shouldFocus: false });
          onError?.(new Error(message));
        }
      }
    },
    [
      clearErrors,
      createUserProfile,
      onAuthenticateSuccess,
      onError,
      promotionKey,
      referralKey,
      registerUser,
      setError,
      traceId,
    ],
  );

  return {
    formState,
    hasInputError,
    register,
    validationState,
    isLoading: isRegisteringUser || isCreatingUserProfile,
    onSubmit: handleSubmit(submitForm, onError),
  };
}
